import { ESuggestedProductViewId } from 'core/model/enums/catalog.enum';
import { ECartActions, ECartError } from 'core/model/enums/shopping-cart.enum';
import { shoppingCartService } from 'core/services/shopping-cart/shopping-cart.service';
import type { TSliceExtraReducer } from 'core/store';
import { refreshCheckoutDetailAction } from 'core/store/checkout';
import { getCustomerIdFromCheckout } from 'core/store/checkout/checkout-state.utils';
import { createAppAsyncThunk } from 'core/store/store.utils';
import { getSuggestedProducts } from 'core/store/suggested-products';
import { getCustomerIdByProductSource, handleErrorOnChangingProductQuantity } from '../cart-state.utils';
import { CART_ERRORS_INITIAL_STATE, EMPTY_SHOPPING_CART_GROUPS, SLICE_NAMESPACE } from '../constants';
import { ICartState, IChangeProductActionParams } from '../types';
import { getCartAction } from './get-cart';

export const changeProductActionFromCart = createAppAsyncThunk(
  `${SLICE_NAMESPACE}/changeProductFromCartAction`,
  async (payload: IChangeProductActionParams & { shouldRefreshCheckoutDetails: boolean }, { getState, dispatch }) => {
    const { layout, checkout, auth } = getState();
    const checkoutCustomerId = getCustomerIdFromCheckout(checkout);
    const customerIdByProductSource = getCustomerIdByProductSource(auth.customer, payload.product.sourceId);
    const customerId = customerIdByProductSource || checkoutCustomerId;
    const isCartOpen = layout.cart;
    const cartAction = isCartOpen ? { cartAction: ECartActions.REFRESH } : undefined;
    try {
      await shoppingCartService.putShoppingCart(payload, customerId);
      dispatch(getSuggestedProducts({ sectionId: ESuggestedProductViewId.shoppingCart }));
    } catch (error) {
      handleErrorOnChangingProductQuantity(error, () => dispatch(getCartAction(cartAction)));
    }

    if (payload.shouldRefreshCheckoutDetails) dispatch(refreshCheckoutDetailAction());

    try {
      const cart = await shoppingCartService.getShoppingCart(customerId, cartAction);
      return { cart };
    } catch {
      throw new Error(ECartError.SHOW_ERROR_AND_KEEP_CART_OPEN);
    }
  },
);

export const changeProductReducerFromCart: TSliceExtraReducer<ICartState> = builder => {
  builder
    .addCase(changeProductActionFromCart.pending, (state, { meta }) => {
      state.productsLoading = [...state.productsLoading, meta.arg.sku];
      state.summaryLoading = true;
      state.success = false;
      state.errors = CART_ERRORS_INITIAL_STATE;
    })
    .addCase(changeProductActionFromCart.fulfilled, (state, { payload, meta }) => {
      state.itemsGroups = payload.cart.itemsGroups || EMPTY_SHOPPING_CART_GROUPS;
      state.deletedProducts = payload.cart.deletedProducts;
      state.amounts = payload.cart.amounts;
      state.productsLoading = state.productsLoading.filter(pc => pc !== meta.arg.sku);
      state.errors = { userValidation: payload.cart.errorType, getData: null };
      state.summaryLoading = false;
      state.success = true;
      state.isValid = payload.cart.isValid;
    })
    .addCase(changeProductActionFromCart.rejected, (state, { error, meta }) => {
      state.summaryLoading = false;
      state.productsLoading = state.productsLoading.filter(pc => pc !== meta.arg.sku);
      if (error?.message && error?.message !== ECartError.UNEXPECTED_ERROR) state.errors.getData = error.message;
      state.success = false;
    });

  return builder;
};
